import { Col, Divider, Row, Skeleton, Typography } from "antd";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { formatCropCycleOption } from "../../lib/formats/agro";
import { formatDateRange } from "../../lib/formats/dates";
import { useCurrencySwitch } from "../../lib/formats/money";
import {
  CropCycleFragment,
  DateRangeInput as DashboardDateRange,
  DateRangeType,
  WorkOrderType,
} from "../../lib/graphql";
import { usePreviousCropCycles } from "../../lib/hooks/agro/cropCycles";
import { useMediaXs } from "../../lib/hooks/basic/media";
import { useCurrentUser, usePermissions } from "../../lib/hooks/basic/users";
import { useLazyDashboardReport } from "../../lib/hooks/reports/dashboardReport";
import { isRangeType, RangePickerValue } from "../form/DatePicker";
import { SelectField } from "../form/SelectField";
import { Space } from "../shared/Space";
import { DateRangeInput } from "./DateRangeInput";
import { LazyWidget } from "./LazyWidget";
import { MarketingWidget } from "./MarketingWidget";

const ActivityExpensesWidget = dynamic(
  () => import("./ActivityExpensesWidget"),
  {
    loading: () => <Skeleton active />,
    ssr: false,
  }
);

const TotalExpensesWidget = dynamic(() => import("./TotalExpensesWidget"), {
  loading: () => <Skeleton active paragraph={false} />,
  ssr: false,
});

const TotalManDaysWidget = dynamic(() => import("./TotalManDaysWidget"), {
  loading: () => <Skeleton active paragraph={false} />,
  ssr: false,
});

const TopActivityCategoriesWidget = dynamic(
  () => import("./TopActivityCategoriesWidget"),
  {
    loading: () => <Skeleton active paragraph={false} />,
    ssr: false,
  }
);

const TotalMachineHoursWidget = dynamic(
  () => import("./TotalMachineHoursWidget"),
  {
    loading: () => <Skeleton active paragraph={false} />,
    ssr: false,
  }
);

const ManDayActivitiesPie = dynamic(() => import("./ManDayActivitiesPie"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const ManDayPositionPie = dynamic(() => import("./ManDayPositionPie"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const RecentPurchases = dynamic(() => import("./RecentPurchases"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const UpcomingInvoicesTable = dynamic(() => import("./UpcomingInvoicesTable"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const HarvestDataWidget = dynamic(() => import("./HarvestDataWidget"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const HarvestChartWidget = dynamic(() => import("./HarvestChartWidget"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const ExpensesWidget = dynamic(() => import("./ExpensesWidget"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const WorkOrders = dynamic(() => import("./WorkOrders"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

const LocalityOverview = dynamic(() => import("./LocalityOverview"), {
  loading: () => <Skeleton active />,
  ssr: false,
});

export default function Dashboard({
  currentCropCycle: propCropCycle,
}: {
  currentCropCycle?: CropCycleFragment | null;
}) {
  const { currentTenant, currentLocality, currentCropCycle, user } =
    useCurrentUser();
  const cropCycle = propCropCycle || currentCropCycle;
  const showCost = usePermissions((p) => !!p.settings?.showCost);
  const isXs = useMediaXs();
  const { currentCurrency, currencySwitch } = useCurrencySwitch();
  const [documentDate, setDocumentDate] = useState<RangePickerValue>(null);
  const [documentRangeType, setDocumentRangeType] =
    useState<DateRangeType | null>(null);
  const [documentDateRange, setDocumentDateRange] =
    useState<DashboardDateRange>();

  const currencyCode = currentCurrency.isoCode;
  const localityId = currentLocality?.id;
  const cropCycleId = cropCycle?.id;

  const [loadDashboardReport, { loading, workOrders, localitySummary }] =
    useLazyDashboardReport();

  useEffect(() => {
    if (!!currentTenant.localities.length && !currentLocality?.id) return;

    loadDashboardReport({
      variables: {
        localityId,
        cropCycleId,
        documentDate: documentDateRange,
      },
    });
  }, [
    loadDashboardReport,
    currentTenant.localities.length,
    currentLocality?.id,
    localityId,
    cropCycleId,
    documentDateRange,
  ]);

  const previousCropCycles = usePreviousCropCycles();

  const previousCycleOptions = useMemo(
    () =>
      previousCropCycles.map((cycle) => ({
        key: cycle.id,
        value: cycle.id,
        label: `vs ${cycle.name}`,
        cropCycle: cycle,
      })),
    [previousCropCycles]
  );

  const currentComparisonContext = `${localityId || ""}:${cropCycleId || ""}`;
  const [comparisonSelection, setComparisonSelection] = useState<{
    context: string;
    cropCycleId: string | null;
  }>();
  const compareToCropCycleId =
    comparisonSelection?.context === currentComparisonContext
      ? comparisonSelection.cropCycleId
      : previousCropCycles[0]?.id || null;
  const activeCompareToCropCycleId = documentDateRange
    ? null
    : compareToCropCycleId;

  const showExpenses =
    user?.permissions.report?.expense && currentTenant.features.expenses;
  const showCosts = showCost && showExpenses;
  const showMarketingOverlay = currentTenant.planName === "free";
  const showMachinery =
    !!currentTenant.features.machinery || showMarketingOverlay;

  const showExpensesWidget = showCosts || showMarketingOverlay;

  const otherWidgetCount =
    2 + (showExpensesWidget ? 1 : 0) + (showMachinery ? 1 : 0);
  const remainingSpan = 24;
  const baseSpan = Math.floor(remainingSpan / otherWidgetCount);
  const remainder = remainingSpan - baseSpan * otherWidgetCount;

  const getOtherWidgetSpan = (index: number) =>
    index < remainder ? baseSpan + 1 : baseSpan;

  const showHarvestWidget =
    user?.permissions.report?.harvest &&
    (currentTenant.features.enabledWorkOrderTypes?.includes(
      WorkOrderType.Harvest
    ) ||
      showMarketingOverlay);

  const showInventoryStockWidgets =
    !!currentTenant.features.inventoryStock || showMarketingOverlay;

  function applyDateRangeType(rangeType: DateRangeType | null) {
    setDocumentRangeType(rangeType);
    applyDateRange(rangeType, documentDate);
  }

  function applyDocumentDate(date: RangePickerValue) {
    setDocumentDate(date);
    applyDateRange(documentRangeType, date);
  }

  function applyDateRange(
    rangeType: DateRangeType | null,
    range: RangePickerValue
  ) {
    if (isRangeType(rangeType) && !range) return;

    const nextRange = rangeType
      ? {
          rangeType,
          ...(isRangeType(rangeType) ? { range } : {}),
        }
      : undefined;
    setDocumentDateRange(nextRange);
    if (nextRange) {
      setComparisonSelection({
        context: currentComparisonContext,
        cropCycleId: null,
      });
    }
  }

  return (
    <>
      <Space vertical>
        <div
          style={{
            display: "flex",
            flexDirection: isXs ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isXs ? "stretch" : "center",
            gap: 8,
          }}
        >
          <FilterDate
            currentCropCycle={cropCycle}
            documentDateRange={documentDateRange}
          />

          <Space
            size="middle"
            vertical={isXs}
            style={isXs ? { width: "100%" } : undefined}
          >
            <DateRangeInput
              rangeType={documentRangeType}
              setRangeType={applyDateRangeType}
              documentDate={documentDate}
              setDocumentDate={applyDocumentDate}
              rangePickerProps={{
                disabledDate: (date) =>
                  !!cropCycle &&
                  !dayjs(date).isBetween(
                    dayjs(cropCycle.startedAt),
                    dayjs(cropCycle.estimatedEndDate),
                    "date",
                    "[]"
                  ),
              }}
              style={isXs ? { width: "100%" } : { minWidth: "200px" }}
            />

            {!documentDateRange && (
              <SelectField
                value={activeCompareToCropCycleId}
                onChange={(value) => {
                  setComparisonSelection({
                    context: currentComparisonContext,
                    cropCycleId: value || null,
                  });
                }}
                style={isXs ? { width: "100%" } : { width: 160 }}
                placeholder={
                  <FormattedMessage
                    id="dashboard.totalExpenses.comparePlaceholder"
                    defaultMessage="Compare to..."
                  />
                }
                allowClear
                className="rounded"
                options={previousCycleOptions}
                formatOption={formatCropCycleOption}
              />
            )}

            {currencySwitch}
          </Space>
        </div>

        <Row gutter={[8, 8]}>
          {showExpensesWidget && (
            <Col xs={24} md={12} xl={getOtherWidgetSpan(0)}>
              <LazyWidget>
                <TotalExpensesWidget
                  localityId={localityId}
                  cropCycleId={cropCycleId}
                  compareToCropCycleId={activeCompareToCropCycleId}
                  currencyCode={currencyCode}
                  documentDateRange={documentDateRange}
                />
              </LazyWidget>
            </Col>
          )}

          <Col
            xs={24}
            md={12}
            xl={getOtherWidgetSpan(showExpensesWidget ? 1 : 0)}
          >
            <LazyWidget>
              <TopActivityCategoriesWidget
                localityId={localityId}
                cropCycleId={cropCycleId}
                compareToCropCycleId={activeCompareToCropCycleId}
                documentDateRange={documentDateRange}
              />
            </LazyWidget>
          </Col>

          <Col
            xs={24}
            md={12}
            xl={getOtherWidgetSpan(showExpensesWidget ? 2 : 1)}
          >
            <LazyWidget>
              <TotalManDaysWidget
                localityId={localityId}
                cropCycleId={cropCycleId}
                compareToCropCycleId={activeCompareToCropCycleId}
                documentDateRange={documentDateRange}
              />
            </LazyWidget>
          </Col>

          {showMachinery && (
            <Col
              xs={24}
              md={12}
              xl={getOtherWidgetSpan(showExpensesWidget ? 3 : 2)}
            >
              <MarketingWidget
                featureKey="machinery"
                title={
                  <FormattedMessage
                    id="dashboard.upgrade.machinery.title"
                    defaultMessage="Machinery Management"
                  />
                }
                description={
                  <FormattedMessage
                    id="dashboard.upgrade.machinery.description"
                    defaultMessage="Monitor machine usage, track maintenance schedules, and optimize equipment efficiency."
                  />
                }
              >
                <LazyWidget>
                  <TotalMachineHoursWidget
                    localityId={localityId}
                    cropCycleId={cropCycleId}
                    compareToCropCycleId={activeCompareToCropCycleId}
                    documentDateRange={documentDateRange}
                  />
                </LazyWidget>
              </MarketingWidget>
            </Col>
          )}
        </Row>

        <Row gutter={[8, 8]}>
          {showHarvestWidget && (
            <>
              <Col xs={24} md={6}>
                <MarketingWidget
                  featureKey="harvest"
                  title={
                    <FormattedMessage
                      id="dashboard.upgrade.harvest.title"
                      defaultMessage="Harvest Tracking"
                    />
                  }
                  description={
                    <FormattedMessage
                      id="dashboard.upgrade.harvest.description"
                      defaultMessage="Monitor harvest progress, track yields, and analyze crop performance with real-time harvest data."
                    />
                  }
                >
                  <LazyWidget minHeight={378}>
                    <HarvestDataWidget
                      key={currentComparisonContext}
                      documentDateRange={documentDateRange}
                    />
                  </LazyWidget>
                </MarketingWidget>
              </Col>

              <Col xs={24} md={18}>
                <MarketingWidget
                  featureKey="harvest"
                  title={
                    <FormattedMessage
                      id="dashboard.upgrade.harvest.title"
                      defaultMessage="Harvest Tracking"
                    />
                  }
                  description={
                    <FormattedMessage
                      id="dashboard.upgrade.harvest.description"
                      defaultMessage="Monitor harvest progress, track yields, and analyze crop performance with real-time harvest data."
                    />
                  }
                >
                  <LazyWidget minHeight={378}>
                    <HarvestChartWidget
                      key={currentComparisonContext}
                      documentDateRange={documentDateRange}
                    />
                  </LazyWidget>
                </MarketingWidget>
              </Col>
            </>
          )}

          <Col xs={24} md={showExpensesWidget ? 12 : 24}>
            <LazyWidget>
              <ActivityExpensesWidget
                localityId={localityId}
                cropCycleId={cropCycleId}
                compareToCropCycleId={activeCompareToCropCycleId}
                currencyCode={currencyCode}
                documentDateRange={documentDateRange}
              />
            </LazyWidget>
          </Col>

          {showExpensesWidget && (
            <Col xs={24} md={12}>
              <LazyWidget>
                <ExpensesWidget
                  localityId={localityId}
                  cropCycleId={cropCycleId}
                  compareToCropCycleId={activeCompareToCropCycleId}
                  currencyCode={currencyCode}
                  documentDateRange={documentDateRange}
                />
              </LazyWidget>
            </Col>
          )}

          <Col xs={24} md={12}>
            <LazyWidget>
              <ManDayActivitiesPie
                localityId={localityId}
                cropCycleId={cropCycleId}
                compareToCropCycleId={activeCompareToCropCycleId}
                documentDateRange={documentDateRange}
              />
            </LazyWidget>
          </Col>

          <Col xs={24} md={12}>
            <LazyWidget>
              <ManDayPositionPie
                localityId={localityId}
                cropCycleId={cropCycleId}
                compareToCropCycleId={activeCompareToCropCycleId}
                documentDateRange={documentDateRange}
              />
            </LazyWidget>
          </Col>

          {showInventoryStockWidgets && (
            <>
              <Col xs={24} md={12}>
                <MarketingWidget
                  featureKey="purchases"
                  title={
                    <FormattedMessage
                      id="dashboard.upgrade.purchases.title"
                      defaultMessage="Purchases Management"
                    />
                  }
                  description={
                    <FormattedMessage
                      id="dashboard.upgrade.purchases.description"
                      defaultMessage="Manage inventory, track purchases, and monitor upcoming invoices with real-time insights."
                    />
                  }
                >
                  <LazyWidget>
                    <RecentPurchases
                      currencyCode={currencyCode}
                      documentDateRange={documentDateRange}
                    />
                  </LazyWidget>
                </MarketingWidget>
              </Col>

              <Col xs={24} md={12}>
                <MarketingWidget
                  featureKey="invoices"
                  title={
                    <FormattedMessage
                      id="dashboard.upgrade.invoices.title"
                      defaultMessage="Invoice Management"
                    />
                  }
                  description={
                    <FormattedMessage
                      id="dashboard.upgrade.invoices.description"
                      defaultMessage="Manage inventory, track purchases, and monitor upcoming invoices with real-time insights."
                    />
                  }
                >
                  <LazyWidget>
                    <UpcomingInvoicesTable currencyCode={currencyCode} />
                  </LazyWidget>
                </MarketingWidget>
              </Col>
            </>
          )}
        </Row>
      </Space>

      {!!workOrders?.workOrderTypeSummary.length && (
        <>
          <Divider>
            <FormattedMessage id="workOrders" />
          </Divider>

          <WorkOrders
            loading={loading}
            {...workOrders}
            documentDateRange={documentDateRange}
          />
        </>
      )}

      <LocalityOverview
        loading={loading}
        currentLocality={currentLocality}
        {...localitySummary}
      />
    </>
  );
}

function FilterDate({
  currentCropCycle,
  documentDateRange,
}: {
  currentCropCycle?: Pick<
    CropCycleFragment,
    "startedAt" | "closedAt" | "estimatedEndDate"
  > | null;
  documentDateRange?: DashboardDateRange;
}) {
  const intl = useIntl();
  const range = documentDateRange?.range;
  const documentRangeType = documentDateRange?.rangeType || null;

  const dateRange = useMemo(() => {
    if (range) {
      return {
        start: dayjs(range[0]),
        end: dayjs(range[1]),
      };
    }

    if (!currentCropCycle) {
      return {
        start: dayjs().startOf("year"),
        end: dayjs().endOf("year"),
      };
    }

    return {
      start: dayjs(currentCropCycle.startedAt),
      end: dayjs(
        currentCropCycle.closedAt || currentCropCycle.estimatedEndDate
      ),
    };
  }, [currentCropCycle, range]);

  return (
    <div>
      <Typography.Text style={{ fontWeight: 600 }}>
        <FormattedMessage id="dates" defaultMessage="Dates" />:
      </Typography.Text>{" "}
      {documentRangeType &&
      documentRangeType !== DateRangeType.Range &&
      documentRangeType !== DateRangeType.MonthRange
        ? intl.formatMessage({
            id: `dateRanges.types.${documentRangeType}`,
          })
        : formatDateRange(dateRange.start.toDate(), dateRange.end.toDate())}
    </div>
  );
}
